import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import {
  getLiveCameraAccessStatus,
  getLiveCameraStreamUrl,
  lockLiveCameras,
  requestLiveCameraAccessCode,
  unlockLiveCameras,
} from "../../../api/sevkiyatCamera/SevkiyatCamApi";
import "./ShipmentLiveCameras.css";

const formatRemainingTime = (seconds) => {
  const safeSeconds = Math.max(seconds, 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const rest = safeSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(rest).padStart(2, "0")}`;
};

function ShipmentLiveCameras({ standalone = false }) {
  const { user } = useAuth();
  const cameraStageRef = useRef(null);
  const videoRefs = useRef({});
  const recoveryTimers = useRef({});
  const playbackProgress = useRef({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [codeSent, setCodeSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");
  const [streamErrors, setStreamErrors] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [streamRefreshKeys, setStreamRefreshKeys] = useState({});

  const cameras = useMemo(() => access?.cameras || [], [access]);
  const userFullName =
    [user?.name, user?.surname].filter(Boolean).join(" ") || "Bilinmeyen kullanıcı";

  useEffect(() => {
    let isMounted = true;

    const checkExistingAccess = async () => {
      try {
        const response = await getLiveCameraAccessStatus();
        const payload = response.data?.data || response.data;

        if (isMounted && payload?.active) {
          setAccess(payload);
          setIsExpanded(true);
        }
      } catch (requestError) {
        console.error("Kamera erişim durumu alınamadı:", requestError);
      } finally {
        if (isMounted) setIsCheckingAccess(false);
      }
    };

    checkExistingAccess();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement;

      setIsFullscreen(fullscreenElement === cameraStageRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!access?.expiresAt) return undefined;

    const updateRemainingTime = () => {
      const now = Date.now();
      const remaining = Math.max(
        Math.ceil((new Date(access.expiresAt).getTime() - now) / 1000),
        0
      );

      setCurrentDateTime(new Date(now));
      setRemainingSeconds(remaining);

      if (remaining === 0) {
        setAccess(null);
        setIsExpanded(false);
        setStreamErrors({});
      }
    };

    updateRemainingTime();
    const timer = window.setInterval(updateRemainingTime, 1000);

    return () => window.clearInterval(timer);
  }, [access?.expiresAt]);

  const cancelCameraRecovery = useCallback((cameraId) => {
    if (recoveryTimers.current[cameraId]) {
      window.clearTimeout(recoveryTimers.current[cameraId]);
      delete recoveryTimers.current[cameraId];
    }
  }, []);

  const refreshCameraStream = useCallback((cameraId) => {
    cancelCameraRecovery(cameraId);
    delete playbackProgress.current[cameraId];
    setStreamErrors((current) => ({ ...current, [cameraId]: false }));
    setStreamRefreshKeys((current) => ({
      ...current,
      [cameraId]: (current[cameraId] || 0) + 1,
    }));
  }, [cancelCameraRecovery]);

  const scheduleCameraRecovery = useCallback((cameraId, delay = 4000) => {
    if (recoveryTimers.current[cameraId]) return;

    recoveryTimers.current[cameraId] = window.setTimeout(() => {
      refreshCameraStream(cameraId);
    }, delay);
  }, [refreshCameraStream]);

  useEffect(() => {
    if (!access || !isExpanded) return undefined;

    const monitor = window.setInterval(() => {
      const now = Date.now();

      cameras.forEach((camera) => {
        const video = videoRefs.current[camera.id];
        if (!video) return;

        const previous = playbackProgress.current[camera.id];
        const currentTime = Number(video.currentTime) || 0;

        if (!previous || currentTime > previous.currentTime + 0.1) {
          playbackProgress.current[camera.id] = {
            currentTime,
            lastProgressAt: now,
          };
          cancelCameraRecovery(camera.id);
          return;
        }

        if (!video.paused && now - previous.lastProgressAt >= 8000) {
          scheduleCameraRecovery(camera.id, 0);
        }
      });
    }, 2000);

    return () => {
      window.clearInterval(monitor);
      Object.values(recoveryTimers.current).forEach((timer) =>
        window.clearTimeout(timer)
      );
      recoveryTimers.current = {};
      playbackProgress.current = {};
    };
  }, [access, cameras, cancelCameraRecovery, isExpanded, scheduleCameraRecovery]);

  const handleRequestCode = async () => {
    setIsRequestingCode(true);
    setError("");

    try {
      const response = await requestLiveCameraAccessCode();
      const payload = response.data?.data || response.data;

      setCodeSent(true);
      setMaskedEmail(payload?.maskedEmail || "kayıtlı e-posta adresinize");
      setResendSeconds(payload?.resendAfterSec || 60);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.response?.data?.error ||
          "Doğrulama kodu gönderilemedi."
      );
    } finally {
      setIsRequestingCode(false);
    }
  };

  const openCameraArea = () => {
    setError("");

    if (access) {
      setIsExpanded((current) => !current);
      return;
    }

    setVerificationCode("");
    setCodeSent(false);
    setMaskedEmail("");
    setResendSeconds(0);
    setShowVerificationModal(true);
    handleRequestCode();
  };

  const handleUnlock = async (event) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(verificationCode)) {
      setError("E-postanıza gelen 6 haneli doğrulama kodunu girin.");
      return;
    }

    setIsUnlocking(true);
    setError("");

    try {
      const response = await unlockLiveCameras(verificationCode);
      const payload = response.data?.data || response.data;

      setAccess(payload);
      setIsExpanded(true);
      setStreamErrors({});
      setVerificationCode("");
      setShowVerificationModal(false);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          requestError.response?.data?.error ||
          "Kamera erişimi açılamadı."
      );
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleLock = async () => {
    setAccess(null);
    setIsExpanded(false);
    setStreamErrors({});

    try {
      await lockLiveCameras();
    } catch (requestError) {
      console.error("Kamera oturumu kapatılamadı:", requestError);
    }
  };

  const handleFullscreen = async () => {
    const stage = cameraStageRef.current;

    if (!stage) return;

    try {
      if (stage.requestFullscreen) {
        await stage.requestFullscreen();
      } else if (stage.webkitRequestFullscreen) {
        stage.webkitRequestFullscreen();
      }
    } catch (fullscreenError) {
      console.error("Tam ekran modu açılamadı:", fullscreenError);
    }
  };

  return (
    <section
      className={`shipment-live-cameras${
        standalone ? " shipment-live-cameras--standalone" : ""
      } mb-3`}
      aria-label="Canlı sevkiyat kameraları"
    >
      <Card className="border-0 shadow-sm overflow-hidden">
        <Card.Body className="p-3">
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <span className="shipment-live-cameras__icon" aria-hidden="true">
                ●
              </span>
              <div>
                <div className="fw-bold text-dark">Canlı Sevkiyat Kameraları</div>
                <small className="text-muted">
                  Yetkili kullanıcılar e-posta doğrulamasıyla yayınları 9 saat açabilir.
                </small>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {access && (
                <Badge bg="light" text="dark" className="border fw-normal">
                  Kalan süre {formatRemainingTime(remainingSeconds)}
                </Badge>
              )}

              {access && (
                <Button size="sm" variant="outline-danger" onClick={handleLock}>
                  Erişimi Kapat
                </Button>
              )}

              {standalone && access && isExpanded && (
                <Button size="sm" variant="outline-dark" onClick={handleFullscreen}>
                  ⛶ Tam Ekran
                </Button>
              )}

              <Button
                size="sm"
                variant={isExpanded ? "outline-primary" : "primary"}
                onClick={openCameraArea}
                disabled={isCheckingAccess}
              >
                {isCheckingAccess ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Kontrol Ediliyor
                  </>
                ) : isExpanded ? (
                  "Kameraları Gizle"
                ) : (
                  "Kameraları Göster"
                )}
              </Button>
            </div>
          </div>
        </Card.Body>

        {access && isExpanded && (
          <div
            ref={cameraStageRef}
            className={`shipment-live-cameras__stage border-top bg-light p-3${
              isFullscreen ? " shipment-live-cameras__stage--fullscreen" : ""
            }`}
          >
            <div className="shipment-live-cameras__grid">
              {cameras.map((camera) => (
                <article className="shipment-live-cameras__item" key={camera.id}>
                  <div className="shipment-live-cameras__video-wrap">
                    <video
                      key={`${camera.id}-${streamRefreshKeys[camera.id] || 0}`}
                      ref={(element) => {
                        if (element) videoRefs.current[camera.id] = element;
                        else delete videoRefs.current[camera.id];
                      }}
                      className="shipment-live-cameras__video"
                      src={getLiveCameraStreamUrl(camera.id)}
                      autoPlay
                      muted
                      playsInline
                      controls
                      crossOrigin="use-credentials"
                      onCanPlay={(event) => {
                        cancelCameraRecovery(camera.id);
                        setStreamErrors((current) => ({
                          ...current,
                          [camera.id]: false,
                        }));
                        event.currentTarget.play().catch(() => {});
                      }}
                      onPlaying={() => cancelCameraRecovery(camera.id)}
                      onWaiting={() => scheduleCameraRecovery(camera.id)}
                      onStalled={() => scheduleCameraRecovery(camera.id)}
                      onEnded={() => scheduleCameraRecovery(camera.id, 0)}
                      onError={() => {
                        setStreamErrors((current) => ({
                          ...current,
                          [camera.id]: true,
                        }));
                        scheduleCameraRecovery(camera.id, 2000);
                      }}
                    />

                    {streamErrors[camera.id] && (
                      <div className="shipment-live-cameras__offline">
                        Bu kameranın yayınına şu anda ulaşılamıyor.
                      </div>
                    )}
                  </div>

                  <div className="shipment-live-cameras__camera-caption d-flex align-items-center justify-content-between gap-2 px-3 py-2 bg-white">
                    <span className="fw-semibold small">{camera.label}</span>
                    <Badge bg={camera.group === "KANTAR" ? "primary" : "success"}>
                      {camera.group === "KANTAR" ? "Kantar" : "Sevkiyat"}
                    </Badge>
                  </div>
                </article>
              ))}
            </div>

            {standalone && (
              <footer className="shipment-live-cameras__session-info" aria-label="Kamera oturum bilgileri">
                <span>
                  <strong>Kullanıcı:</strong> {userFullName}
                </span>
                <span>
                  <strong>Tarih:</strong>{" "}
                  {currentDateTime.toLocaleDateString("tr-TR")}
                </span>
                <span>
                  <strong>Saat:</strong>{" "}
                  {currentDateTime.toLocaleTimeString("tr-TR")}
                </span>
                <span>
                  <strong>Kalan erişim süresi:</strong>{" "}
                  {formatRemainingTime(remainingSeconds)}
                </span>
                {isFullscreen && (
                  <span className="shipment-live-cameras__fullscreen-hint">
                    Tam ekrandan çıkmak için Esc tuşuna basın
                  </span>
                )}
              </footer>
            )}
          </div>
        )}
      </Card>

      <Modal
        show={showVerificationModal}
        onHide={() =>
          !isUnlocking && !isRequestingCode && setShowVerificationModal(false)
        }
        centered
        backdrop="static"
        keyboard={!isUnlocking && !isRequestingCode}
      >
        <Form onSubmit={handleUnlock}>
          <Modal.Header closeButton={!isUnlocking && !isRequestingCode}>
            <Modal.Title className="fs-5">Kamera Erişimini Doğrula</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="text-muted small mb-3">
              Canlı yayın doğrulama sonrasında 9 saat açık kalır. Erişim kodu yalnızca
              kamera izleme yetkisi bulunan hesabınızın e-posta adresine gönderilir.
            </p>

            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

            {isRequestingCode && !codeSent && (
              <div className="text-center py-3 text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Doğrulama kodu gönderiliyor...
              </div>
            )}

            {codeSent && (
              <Alert variant="success" className="py-2 small">
                Kod <strong>{maskedEmail}</strong> adresine gönderildi. Kod 10 dakika
                geçerlidir.
              </Alert>
            )}

            <Form.Group controlId="shipmentCameraVerificationCode">
              <Form.Label className="fw-semibold">Doğrulama Kodu</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verificationCode}
                onChange={(event) =>
                  setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                autoComplete="one-time-code"
                placeholder="000000"
                autoFocus
                disabled={isUnlocking || isRequestingCode || !codeSent}
              />
            </Form.Group>

            <div className="mt-3">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={handleRequestCode}
                disabled={isRequestingCode || resendSeconds > 0}
              >
                {isRequestingCode
                  ? "Kod gönderiliyor..."
                  : resendSeconds > 0
                    ? `Yeni kod için ${resendSeconds} saniye bekleyin`
                    : codeSent
                      ? "Yeni kod gönder"
                      : "Doğrulama kodunu tekrar gönder"}
              </Button>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowVerificationModal(false)}
              disabled={isUnlocking || isRequestingCode}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isUnlocking || isRequestingCode || !codeSent}
            >
              {isUnlocking ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Doğrulanıyor...
                </>
              ) : (
                "Kodu Doğrula ve Yayını Aç"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
}

export default ShipmentLiveCameras;
